import { useEffect, useState, useRef } from 'react';

interface UseStepAnimationOptions {
  totalSteps: number;
  stepDelay?: number;
  onStepComplete?: (stepIndex: number) => void;
}

interface UseStepAnimationReturn {
  currentStep: number;
  isComplete: boolean;
}

const DEFAULT_STEP_DELAY = 2000; // 2 seconds per step

export function useStepAnimation({
  totalSteps,
  stepDelay = DEFAULT_STEP_DELAY,
  onStepComplete,
}: UseStepAnimationOptions): UseStepAnimationReturn {
  const [currentStep, setCurrentStep] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);
  const hasStartedRef = useRef(false);

  useEffect(() => {
    if (totalSteps === 0 || hasStartedRef.current) {
      return;
    }

    hasStartedRef.current = true;
    setCurrentStep(0);
    setIsComplete(false);

    let stepIndex = 0;

    const advanceStep = () => {
      if (stepIndex < totalSteps - 1) {
        stepIndex += 1;
        setCurrentStep(stepIndex);
        onStepComplete?.(stepIndex);
      } else {
        // Last step is active, wait one more interval before marking as complete
        setIsComplete(true);
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
          intervalRef.current = null;
        }
      }
    };

    intervalRef.current = setInterval(advanceStep, stepDelay);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [totalSteps, stepDelay, onStepComplete]);

  return { currentStep, isComplete };
}

